import Quill from 'quill';
import Delta from 'quill-delta';
import * as sharedb from 'sharedb';

import Graph from './Graph';
import { showAlert } from '../ui/uiUtils';
import { openNotepad } from '../ui/notepad';
import * as hints from '../tutorials/hints';
import { isMiniTutorialHidden } from '../tutorials/miniTutorial';
import { startSynthesiseTutorial } from '../tutorials/synthesiseTutorial';
import * as tutorialEvents from '../tutorials/tutorialEvents';
import * as templateTutorial from '../tutorials/templateTutorial';
import {
	AvailableColour,
	NCGraphData,
	NCNode,
	BoardUser,
	GraphMode,
	SynthesiseRecipe,
	UserSelection,
} from '../../../../src/commonTypes';
import { openSynthesiseAiPanelForNode } from '../ui/synthesiseAi';
import { uploadBoardImage } from '../apiRequests';
import { applyDefaultGraphNodeShape } from '../../../../src/graphNodeShape';
import { canReuseSynthesiseOutputNode } from '../ui/synthesiseBoardTarget';
import { findFreeSpaceRadial, proposePointNearInputs } from './geometryUtils';

class MoodboardGraph extends Graph {
	public quill: Quill;

	constructor(
		data: NCGraphData,
		boardUsers: Array<BoardUser>,
		userSelections: Array<UserSelection>,
		colourClass: AvailableColour,
		colourHex: string,
		isCollaborative: boolean,
		automation: boolean,
		notesDoc: sharedb.Doc
	) {
		super(data, boardUsers, userSelections, colourClass, colourHex, isCollaborative, automation);

		document.querySelector('#notepad-container h2')!.innerHTML = isCollaborative
			? 'Team Note'
			: 'Personal Note';
		const shareNoteButton = document.querySelector<HTMLButtonElement>('#share-note')!;
		const notePadLoadingIndicator = document.querySelector<HTMLDivElement>(
			'#notepad-container .loading'
		)!;
		if (isCollaborative) {
			shareNoteButton.classList.add('hidden');
		} else {
			shareNoteButton.classList.remove('hidden');
			shareNoteButton.onclick = (ev) => {
				ev.preventDefault();
				// Show notepad loading indicator when user clicks 'Share to team notepad' button
				notePadLoadingIndicator.classList.remove('hidden');

				const selection = this.quill.getSelection();
				if (selection && selection.length > 0) {
					const contentDelta = this.quill.getContents(selection.index, selection.length);
					if (globalThis.neuroCreate.switchToMode) {
						globalThis.neuroCreate.switchToMode('moodboard', true, automation, () => {
							window.location.hash = '#synthesise-team';
							setTimeout(() => {
								// Insert selected text into team notepad
								const teamGraph = globalThis.neuroCreate.graph as MoodboardGraph;
								teamGraph.quill.updateContents(
									new Delta([
										{ retain: teamGraph.quill.getLength() },
										{ insert: '\n' },
										...contentDelta.ops,
									]),
									'user'
								);
								// Scroll to bottom
								const qlEditor = document.querySelector('.ql-editor')!;
								qlEditor.scrollTop = qlEditor.scrollHeight;
							}, 100);
						});

						// Hide notepad loading indicator once the team notepad is ready
						setTimeout(() => {
							notePadLoadingIndicator.classList.add('hidden');
						}, 500);
					}
				} else {
					// Hide notepad loading indicator immediately if no text is selected
					notePadLoadingIndicator.classList.add('hidden');
					showAlert(
						'Please first select some text in the notepad to share to the team notepad.<br><br>Then, press the share button again and the selected text will be copied into the team notepad.'
					);
				}
			};
		}

		document.querySelector('#notepad-container .ql-toolbar')?.remove();

		this.quill = new Quill('#editor', {
			theme: 'snow',
			modules: {
				toolbar: {
					container: [
						[{ header: 1 }, { header: 2 }, 'bold', 'italic', 'underline', 'strike', { color: [] }],
						['image'],
					],
					handlers: {
						image: this.quillImageHandler.bind(this),
					},
				},
			},
		});
		this.quill.on('text-change', (delta, oldDelta, source) => {
			if (source !== 'user') return;

			notesDoc.submitOp(delta, { source: this.quill });
		});

		notesDoc.subscribe((err) => {
			if (err) {
				throw err;
			}

			if (notesDoc.data === undefined) {
				notesDoc.create([{ insert: '' }], 'http://sharejs.org/types/rich-text/v1');
				console.log('Created empty notes');
			}

			this.quill.setContents(notesDoc.data as ReturnType<typeof this.quill.setContents>);

			notesDoc.on('op', (op, source) => {
				if (source === this.quill) {
					return;
				}

				this.quill.updateContents(op as unknown as ReturnType<typeof this.quill.setContents>);
			});
		});
	}

	quillImageHandler() {
		const input = document.createElement('input');

		input.setAttribute('type', 'file');
		input.setAttribute('accept', 'image/png,image/jpeg');
		input.click();

		input.onchange = async () => {
			const file = input.files && input.files[0];
			if (file) {
				// Save current cursor state
				const range = this.quill.getSelection(true);

				// Insert temporary loading image
				this.quill.insertEmbed(
					range.index,
					'image',
					`${window.location.origin}/assets/loading-large.gif`
				);

				// Move cursor to right side of image (easier to continue typing)
				this.quill.setSelection({ index: range.index + 1, length: 0 });

				const imageUrl = await uploadBoardImage(document.body.dataset.boardId!, file);

				// Remove loading image
				this.quill.deleteText(range.index, 1);

				// Insert uploaded image & save
				this.quill.insertEmbed(range.index, 'image', imageUrl, 'user');
			}
		};
	}

	isEditable(): boolean {
		return !this.activeNode || this.isActiveNodeOwnedByCurrentUser();
	}

	getCurrentlySelectedText(): string {
		const selection = this.quill.getSelection();
		if (selection && selection.length > 1) {
			const contentDelta = this.quill.getText(selection.index, selection.length);
			return contentDelta.replace(/\n+/g, ' ');
		}
		return '';
	}

	validate(): boolean {
		if (this.automation) {
			// Skip possible alerts
			return true;
		}
		const likedItems = document.querySelector('#hearted-container .idea-library-grid');
		if (!this.isCollaborative && likedItems?.querySelector('.empty-list')) {
			showAlert('Please add some items to your Idea Box, in order to use Synthesise mode.');
			return false;
		}
		return true;
	}

	addSelectionToMoodboard(selection: string | { url: string; isVideo: boolean }): NCNode {
		if (typeof selection === 'string') {
			return this.addNodeNearPoint({ x: -380, y: 50 }, selection, undefined, undefined);
		} else {
			return this.addNodeNearPoint(
				{ x: -380, y: 50 },
				undefined,
				selection.url,
				undefined,
				false,
				selection.isVideo
			);
		}
	}

	materialiseSynthesiseOutput(
		result: string,
		task: string,
		recipe: SynthesiseRecipe,
		existingNode?: NCNode
	): NCNode {
		const isImage = task.includes('Image');
		const isVideo = task.includes('Video');
		const shouldReuse = canReuseSynthesiseOutputNode({
			existingTask: existingNode?.synthesiseRecipe?.task,
			nextTask: task,
		});
		const previousRecipe = shouldReuse ? existingNode?.synthesiseRecipe : undefined;

		const uniqueSourceNodes = recipe.inputs
			.map((input) => this.nodes.find((node) => node.uid === input.nodeUid))
			.filter(
				(node): node is NCNode =>
					Boolean(node && node.uid !== existingNode?.uid)
			)
			.filter(
				(node, index, nodes) => nodes.findIndex((candidate) => candidate.uid === node.uid) === index
			);

		let outputNode: NCNode;
		if (shouldReuse && existingNode) {
			delete existingNode.label;
			delete existingNode.src;
			delete existingNode.isVideo;
			delete existingNode.style;
			if (isImage || isVideo) {
				existingNode.src = result;
				existingNode.isVideo = isVideo;
			} else {
				existingNode.label = result;
			}
			applyDefaultGraphNodeShape(this.graphMode, existingNode);
			outputNode = existingNode;
		} else {
			const proposed = proposePointNearInputs(uniqueSourceNodes);
			const freePoint = findFreeSpaceRadial(
				this.nodes,
				this.links,
				proposed,
				uniqueSourceNodes[0],
				this.nodeRadius,
				true
			);
			outputNode = this.addNodeNearPoint(
				freePoint,
				isImage || isVideo ? undefined : result,
				isImage || isVideo ? result : undefined,
				undefined,
				false,
				isVideo
			);
		}

		outputNode.synthesiseRecipe = recipe;
		outputNode.topics = [
			...new Set([
				...(outputNode.topics || []).filter(
					(topic) => !topic.startsWith('synthesise-task:') && !topic.startsWith('source:')
				),
				`source:${task.toLowerCase()}`,
				`synthesise-task:${task}`,
			]),
		];
		globalThis.neuroCreate.saveNodes?.update([outputNode]);

		const priorSourceIds = new Set(
			(previousRecipe?.inputs || []).map((input) => input.nodeUid).filter(Boolean)
		);
		const oldRecipeLinks = this.links.filter(
			(link) =>
				(link.source.uid === outputNode.uid && priorSourceIds.has(link.target.uid)) ||
				(link.target.uid === outputNode.uid && priorSourceIds.has(link.source.uid))
		);
		if (oldRecipeLinks.length > 0) globalThis.neuroCreate.saveLinks?.delete(oldRecipeLinks);

		if (uniqueSourceNodes.length > 0) {
			globalThis.neuroCreate.saveLinks?.create(
				uniqueSourceNodes.map((sourceNode) => ({
					source: outputNode,
					target: sourceNode,
					colour: this.userColourClass,
				}))
			);
		}

		this.updateActive(outputNode);
		return outputNode;
	}

	addToNote(node: NCNode): void {
		openNotepad();

		const teamGraph = globalThis.neuroCreate.graph as MoodboardGraph;
		if (node.label) {
			// Insert node text into team notepad
			const delta = new Delta([
				{ retain: teamGraph.quill.getLength() },
				{ insert: '\n' },
				{ insert: node.label },
			]);
			if (node.href) {
				delta.push({ insert: '\n' });
				delta.push({ insert: node.href });
			}
			teamGraph.quill.updateContents(delta, 'user');
		} else if (node.src) {
			teamGraph.quill.updateContents(
				new Delta([
					{ retain: teamGraph.quill.getLength() },
					{ insert: '\n' },
					{ insert: node.isVideo ? { video: node.src } : { image: node.src } },
				]),
				'user'
			);
		}
		// Scroll to bottom
		const qlEditor = document.querySelector('.ql-editor')!;
		qlEditor.scrollTop = qlEditor.scrollHeight;

		tutorialEvents.addedToNote(node);
	}

	copyMediaToNote(type: 'image' | 'video', url: string): void {
		// Prepare the media insert object based on type.
		const insertObject = type === 'image' ? { image: url } : { video: url };

		// Build Delta operations.
		// We start with a newline to ensure we’re at a new block.
		// Then we insert the media.
		// Finally, if it's a video, we add another newline so it gets wrapped in a <p> tag.
		const deltaOps = [
			{ retain: this.quill.getLength() },
			{ insert: '\n' },
			{ insert: insertObject },
		];

		const delta = new Delta(deltaOps);

		this.quill.updateContents(delta, 'user');

		// Scroll to bottom
		const qlEditor = document.querySelector('.ql-editor')!;
		qlEditor.scrollTop = qlEditor.scrollHeight;
	}

	copyToNote(text: string): void {
		// Insert text into notepad
		const delta = new Delta([
			{ retain: this.quill.getLength() },
			{ insert: '\n' },
			{ insert: text },
		]);
		this.quill.updateContents(delta, 'user');
		// Scroll to bottom
		const qlEditor = document.querySelector('.ql-editor')!;
		qlEditor.scrollTop = qlEditor.scrollHeight;
	}

	getMenuOptions(): Array<{
		label: string;
		enabled: boolean;
		requiresAi?: boolean;
		longText?: boolean;
		shortText: boolean;
	}> {
		const defaultOptions = { longText: true, shortText: true };
		const menus: Array<{
			label: string;
			enabled: boolean;
			requiresAi?: boolean;
			longText?: boolean;
			shortText: boolean;
		}> = [{ label: 'Like', enabled: true, ...defaultOptions }];

		// If the active node is a video, add Synthesise (disabled)
		if (!this.activeNode?.isVideo) {
			menus.push({
				label: 'Synthesise',
				enabled: true,
				requiresAi: true,
				...defaultOptions,
			});
		}

		menus.push({ label: 'Note', enabled: true, ...defaultOptions });

		menus.push({ label: 'Share', enabled: !this.isCollaborative, ...defaultOptions });

		menus.push({ label: 'Connect', enabled: true, ...defaultOptions });

		return menus;
	}

	/**
	 * Share this node to team board
	 */
	share = (node: NCNode): void => {
		if (!globalThis.neuroCreate.switchToMode) return;

		// Snapshot before the personal graph is torn down / rebound.
		const shared: {
			label?: string;
			src?: string;
			href?: string;
			isVideo: boolean;
			x: number;
			y: number;
			style?: 'rect';
			nodeColour?: NCNode['nodeColour'];
			topics?: string[];
			likes: { [key: string]: boolean };
			synthesiseRecipe?: NCNode['synthesiseRecipe'];
			positioningOutput?: NCNode['positioningOutput'];
			pdfInsightId?: string;
			pdfAnalysisId?: string;
			pdfFileName?: string;
			pdfProvenance?: NCNode['pdfProvenance'];
		} = {
			label: (node.label || '').trim() || undefined,
			src: node.src || undefined,
			href: node.href || undefined,
			isVideo: Boolean(node.isVideo),
			x: typeof node.x === 'number' && Number.isFinite(node.x) ? node.x : 0,
			y: typeof node.y === 'number' && Number.isFinite(node.y) ? node.y : 0,
			style: node.style,
			nodeColour: node.nodeColour,
			topics: node.topics ? [...node.topics] : undefined,
			likes: { ...(node.likes || {}) },
			synthesiseRecipe: node.synthesiseRecipe
				? (structuredClone(node.synthesiseRecipe) as NCNode['synthesiseRecipe'])
				: undefined,
			positioningOutput: node.positioningOutput
				? (structuredClone(node.positioningOutput) as NCNode['positioningOutput'])
				: undefined,
			pdfInsightId: node.pdfInsightId,
			pdfAnalysisId: node.pdfAnalysisId,
			pdfFileName: node.pdfFileName,
			pdfProvenance: node.pdfProvenance,
		};
		if (!shared.label && !shared.src && !shared.positioningOutput) {
			showAlert('This item has nothing to share yet.');
			return;
		}

		globalThis.neuroCreate.switchToMode('moodboard', true, this.automation, () => {
			if (window.location.hash !== '#synthesise-team') {
				window.location.hash = '#synthesise-team';
			}

			const teamGraph = globalThis.neuroCreate.graph as MoodboardGraph;
			if (!teamGraph || teamGraph.graphMode !== 'moodboard' || !teamGraph.isCollaborative) {
				showAlert('Could not open Team Synthesise to share this item.');
				return;
			}

			const point = teamGraph.findNearestFreeSpace(
				{ x: shared.x, y: shared.y },
				undefined
			);
			teamGraph.spawnNewNode(
				{
					...point,
					label: shared.label,
					src: shared.src,
					href: shared.href,
					isVideo: shared.isVideo,
					style: shared.style,
					nodeColour: shared.nodeColour,
					topics: shared.topics,
					likes: shared.likes,
					synthesiseRecipe: shared.synthesiseRecipe,
					positioningOutput: shared.positioningOutput,
					pdfInsightId: shared.pdfInsightId,
					pdfAnalysisId: shared.pdfAnalysisId,
					pdfFileName: shared.pdfFileName,
					pdfProvenance: shared.pdfProvenance,
				},
				null,
				true
			);
		});
	};

	areNodesColoured() {
		return true;
	}

	areLinksColoured() {
		return false;
	}

	get nodeRadius() {
		return 64;
	}

	get graphMode(): GraphMode {
		return 'moodboard';
	}

	// Collision stays on; skip link/group layout forces so saved positions are not reshuffled.
	usesStructuralForces() {
		return false;
	}

	startTutorial(): void {
		super.startTutorial();
		if (this.templateId) {
			templateTutorial.start(this.templateId, this, true);
		} else {
			startSynthesiseTutorial();
		}
	}

	startHints() {
		super.startHints();
		if (
			this.nodes.length > 0 ||
			this.currentUser.settings.shown_synthesise ||
			isMiniTutorialHidden('moodboard')
		) {
			this.setHintsShown();
		} else {
			hints.beginSynthesiseHint();
		}
	}

	setHintsShown() {
		const shouldSave = !this.currentUser.settings.shown_synthesise;
		super.setHintsShown();
		if (!shouldSave) return;
		this.currentUser.settings.shown_synthesise = true;
		this.saveUser();
	}

	getHintsShown(): boolean {
		return this.currentUser.settings.shown_synthesise || false;
	}

	synthesise(node: NCNode): void {
		openSynthesiseAiPanelForNode(node);
		tutorialEvents.clickedSynthesise(node);
	}
}

export default MoodboardGraph;
